import { useState, useEffect, useCallback } from 'react';
import { X, Bell, FileText, MessageSquare, CreditCard, User, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Alert type configurations
const ALERT_TYPES = {
  new_application: {
    icon: FileText,
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-800',
    label: 'New Application',
  },
  new_ach: {
    icon: CreditCard,
    color: 'bg-green-500',
    bgColor: 'bg-green-50 border-green-200',
    textColor: 'text-green-800',
    label: 'ACH Submission',
  },
  new_message: {
    icon: MessageSquare,
    color: 'bg-purple-500',
    bgColor: 'bg-purple-50 border-purple-200',
    textColor: 'text-purple-800',
    label: 'New Message',
  },
  new_client: {
    icon: User,
    color: 'bg-amber-500',
    bgColor: 'bg-amber-50 border-amber-200',
    textColor: 'text-amber-800',
    label: 'New Client',
  },
  new_communication: {
    icon: Bell,
    color: 'bg-indigo-500',
    bgColor: 'bg-indigo-50 border-indigo-200',
    textColor: 'text-indigo-800',
    label: 'New Communication',
  },
  default: {
    icon: AlertCircle,
    color: 'bg-slate-500',
    bgColor: 'bg-slate-50 border-slate-200',
    textColor: 'text-slate-800',
    label: 'Alert',
  },
};

// Format time ago
const timeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return date.toLocaleDateString();
};

const AlertBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [lastCheck, setLastCheck] = useState(null);

  // Check if user is internal PTC user
  const isInternalUser = user?.company_name === 'Prestigious Travel Club Inc' || 
                         user?.company_name === 'PTC' ||
                         user?.role === 'admin' || 
                         user?.role === 'ptc_admin' ||
                         user?.role === 'ptc_director' ||
                         user?.role === 'ptc_manager' ||
                         user?.role === 'director' ||
                         user?.role === 'manager';

  // Fetch recent alerts
  const fetchAlerts = useCallback(async () => {
    // v3.14-108: Only fetch if user is authenticated and internal
    // v3.14-119: Standardized to access_token key
    const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
    if (!token || !user || !isInternalUser) return;

    try {
      const response = await api.get('/alerts/recent');
      const newAlerts = response.data.alerts || [];
      
      // Filter out dismissed alerts
      const activeAlerts = newAlerts.filter(alert => !dismissed.has(alert.id));
      setAlerts(activeAlerts);
      setLastCheck(new Date().toISOString());
    } catch (error) {
      // Silently fail - alerts are not critical
      // Don't log 401 errors as they're expected during auth transitions
      if (error.response?.status !== 401) {
        console.debug('Failed to fetch alerts:', error);
      }
    }
  }, [user, isInternalUser, dismissed]);

  // Poll for new alerts every 15 seconds
  useEffect(() => {
    if (!user || !isInternalUser) return;

    // Initial fetch
    fetchAlerts();

    // Set up polling
    const interval = setInterval(fetchAlerts, 15000);

    return () => clearInterval(interval);
  }, [user, isInternalUser, fetchAlerts]);

  // Dismiss an alert
  const dismissAlert = (alertId) => {
    setDismissed(prev => new Set([...prev, alertId]));
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    
    // Optionally mark as read in backend
    api.post(`/alerts/${alertId}/dismiss`).catch(() => {});
  };

  // Dismiss all alerts
  const dismissAll = () => {
    const allIds = alerts.map(a => a.id);
    setDismissed(prev => new Set([...prev, ...allIds]));
    setAlerts([]);
    
    // Optionally mark all as read in backend
    api.post('/alerts/dismiss-all').catch(() => {});
  };

  // Don't render if no user, not internal, or no alerts
  if (!user || !isInternalUser || alerts.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] pointer-events-none"
      data-testid="alert-banner-container"
    >
      <div className="max-w-7xl mx-auto px-4 pt-2 space-y-2 pointer-events-auto">
        {alerts.slice(0, 5).map((alert) => {
          const config = ALERT_TYPES[alert.type] || ALERT_TYPES.default;
          const Icon = config.icon;
          
          return (
            <div
              key={alert.id}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg animate-slide-down ${config.bgColor}`}
              data-testid={`alert-${alert.id}`}
            >
              {/* Icon */}
              <div className={`p-2 rounded-full ${config.color}`}>
                <Icon className="h-4 w-4 text-white" />
              </div>
              
              {/* Content — clickable to navigate to client */}
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => {
                  if (alert.client_id) navigate(`/clients/${alert.client_id}`);
                  dismissAlert(alert.id);
                }}
                data-testid={`alert-link-${alert.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold uppercase ${config.textColor}`}>
                    {config.label}
                  </span>
                  <span className="text-xs text-slate-500">
                    {timeAgo(alert.created_at)}
                  </span>
                </div>
                <p className={`text-sm font-medium truncate ${config.textColor}`}>
                  {alert.title || alert.message}
                </p>
                {alert.subtitle && (
                  <p className="text-xs text-slate-600 truncate">
                    {alert.subtitle}
                  </p>
                )}
              </div>
              
              {/* Dismiss button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-white/50"
                onClick={() => dismissAlert(alert.id)}
                data-testid={`dismiss-alert-${alert.id}`}
              >
                <X className="h-4 w-4 text-slate-500" />
              </Button>
            </div>
          );
        })}
        
        {/* Dismiss all button if multiple alerts */}
        {alerts.length > 1 && (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-slate-500 hover:text-slate-700"
              onClick={dismissAll}
              data-testid="dismiss-all-alerts"
            >
              Dismiss all ({alerts.length})
            </Button>
          </div>
        )}
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AlertBanner;
